import * as THREE from 'three';

export class TerrainGenerator {
    constructor(scene) {
        this.scene = scene;
        
        // Terrain settings
        this.terrainSize = 1000;
        this.resolution = 128;
        this.maxHeight = 15;
        this.smoothness = 100;
        this.tileSize = 200;
        
        // Array to track terrain chunks
        this.chunks = new Map();
        
        // Material for terrain
        this.material = new THREE.MeshStandardMaterial({
            color: 0xFFFFFF,
            roughness: 0.8,
            metalness: 0.1,
            side: THREE.DoubleSide
        });
        
        // Initial terrain creation
        this.currentChunkCoords = { x: 0, z: 0 };
        this.generateInitialTerrain();
    }
    
    generateInitialTerrain() {
        // Generate 3x3 grid of terrain chunks centered on player
        for (let x = -1; x <= 1; x++) {
            for (let z = -1; z <= 1; z++) {
                this.createTerrainChunk(x, z);
            }
        }
    }
    
    createTerrainChunk(chunkX, chunkZ) {
        const chunkKey = `${chunkX},${chunkZ}`;
        
        // Check if chunk already exists
        if (this.chunks.has(chunkKey)) {
            return;
        }
        
        // Create geometry
        const geometry = new THREE.PlaneGeometry(
            this.tileSize,
            this.tileSize,
            this.resolution,
            this.resolution
        );
        
        // Calculate world position of this chunk
        const worldX = chunkX * this.tileSize;
        const worldZ = chunkZ * this.tileSize;
        
        // Generate height map for terrain
        const vertices = geometry.attributes.position.array;
        for (let i = 0; i < vertices.length; i += 3) {
            // Get original vertex position (local to the plane)
            const x = vertices[i];
            const z = vertices[i + 2];
            
            // Convert to world coordinates for noise calculation
            const worldVertexX = x + worldX;
            const worldVertexZ = z + worldZ;
            
            // Generate height using simplex noise (approximated with sine waves)
            const height = this.generateHeight(worldVertexX, worldVertexZ);
            
            // Apply height to vertex
            vertices[i + 1] = height;
        }
        
        // Update geometry normals
        geometry.computeVertexNormals();
        
        // Create mesh and add to scene
        const mesh = new THREE.Mesh(geometry, this.material);
        mesh.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        mesh.position.set(worldX, 0, worldZ);
        mesh.receiveShadow = true;
        this.scene.add(mesh);
        
        // Store mesh in chunks map
        this.chunks.set(chunkKey, mesh);
    }
    
    // Simple height generation using sine waves (approximating Perlin noise)
    generateHeight(x, z) {
        const scale1 = 0.01;
        const scale2 = 0.02;
        const scale3 = 0.05;
        
        // Use multiple sine waves at different frequencies to create natural-looking terrain
        let height = 0;
        height += Math.sin(x * scale1) * Math.sin(z * scale1) * this.maxHeight * 0.5;
        height += Math.sin(x * scale2 + 0.5) * Math.sin(z * scale2 + 0.5) * this.maxHeight * 0.25;
        height += Math.sin(x * scale3 + 1.0) * Math.sin(z * scale3 + 1.0) * this.maxHeight * 0.125;
        
        // Keep heights generally positive with a minimum height
        return Math.max(0, height + this.maxHeight * 0.25);
    }
    
    update(playerPosition) {
        // Determine which chunk the player is in
        const chunkX = Math.round(playerPosition.x / this.tileSize);
        const chunkZ = Math.round(playerPosition.z / this.tileSize);
        
        // If player has moved to a new chunk
        if (chunkX !== this.currentChunkCoords.x || chunkZ !== this.currentChunkCoords.z) {
            this.currentChunkCoords = { x: chunkX, z: chunkZ };
            
            // Generate new chunks in viewing range
            for (let x = chunkX - 1; x <= chunkX + 1; x++) {
                for (let z = chunkZ - 1; z <= chunkZ + 1; z++) {
                    this.createTerrainChunk(x, z);
                }
            }
            
            // Remove chunks that are too far from player (outside a 5x5 grid)
            this.chunks.forEach((mesh, key) => {
                const [chunkKeyX, chunkKeyZ] = key.split(',').map(Number);
                
                if (Math.abs(chunkKeyX - chunkX) > 2 || Math.abs(chunkKeyZ - chunkZ) > 2) {
                    this.scene.remove(mesh);
                    mesh.geometry.dispose();
                    this.chunks.delete(key);
                }
            });
        }
    }
} 